/* ==========================================================================
   nyx-pdp.js — custom product page behaviour for sections/main-product.liquid
   --------------------------------------------------------------------------
   Root: <product-info class="nyx-pdp" data-section-id="…">.
   Reads inside the root:
     #nyx-variant-data-<section id>   variants JSON (price, compare_at_price)
     [data-nyx-pdp-config]            { moneyFormat, currencySymbol }

   Modules:
     1. Mobile header      rating / stock / price moved above the info column
     2. Price in ADD TO BAG + sticky ADD (old price, current price, -%)
     3. Price + stock on one line
     4. Variant-aware gallery (swap slides from the media pool)
     5. maxmax product labels init
     6. Pay-over-time block moved under shipping on mobile (page-level)
   ========================================================================== */
(function () {
  'use strict';

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); }
    catch (e) { return null; }
  }

  function onDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ---------- 1. Mobile header ---------- */
  function initMobileTop(root, sid) {
    var mq = window.matchMedia('(max-width:768px)');
    var topBar = root.querySelector('[data-nyx-mobile-top]');
    if (!topBar) return;

    var ratingSlot = topBar.querySelector('[data-nyx-mobile-rating-slot]');
    var stockSlot = topBar.querySelector('[data-nyx-mobile-stock-slot]');
    var priceSlot = topBar.querySelector('[data-nyx-mobile-price-slot]');

    function hasContent(el) {
      if (!el) return false;
      var text = (el.textContent || el.innerText || '').trim();
      return text !== '' || !!el.querySelector('*');
    }

    function makeAnchor(el) {
      if (!el || !el.parentNode) return null;
      var a = document.createElement('span');
      a.style.display = 'none';
      el.parentNode.insertBefore(a, el.nextSibling);
      return a;
    }

    function getRatingEl() {
      return root.querySelector(
        '.main-product__block-rating > *, ' +
        '.m-product__rating, .rating, [data-rating], .spr-starrating, .jdgm-prev-badge, .alr-display-review-badge'
      );
    }

    function getStockEl() {
      return root.querySelector(
        '#Inventory-' + sid + ', ' +
        '#Availability-' + sid + ', ' +
        '.m-product__inventory, .inventory, [data-inventory], [data-inventory-count]'
      );
    }

    var rating = getRatingEl();
    var stock = getStockEl();

    var aRating = rating ? makeAnchor(rating) : null;
    var aStock = stock ? makeAnchor(stock) : null;

    function syncPrice() {
      if (!priceSlot) return;
      var source = document.getElementById('Price-' + sid);
      if (!source || !root.contains(source)) return;
      priceSlot.innerHTML = source.innerHTML;
    }

    function moveToMobile() {
      rating = rating || getRatingEl();
      stock = stock || getStockEl();

      if (rating && ratingSlot && hasContent(rating) && rating.parentNode !== ratingSlot) {
        ratingSlot.appendChild(rating);
      }

      if (stock && stockSlot && hasContent(stock) && stock.parentNode !== stockSlot) {
        stockSlot.appendChild(stock);
      }

      syncPrice();
    }

    function restore(el, anchor) {
      if (el && anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(el, anchor);
      }
    }

    function moveToDesktop() {
      restore(rating, aRating);
      restore(stock, aStock);
    }

    function apply() {
      if (mq.matches) {
        moveToMobile();
      } else {
        moveToDesktop();
      }
      syncPrice();
    }

    apply();
    if (document.readyState !== 'complete') {
      window.addEventListener('load', apply, { once: true });
    }

    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
    } else {
      mq.addListener(apply);
    }

    document.addEventListener('variant:changed', function () {
      requestAnimationFrame(function () {
        stock = getStockEl();
        apply();
      });
    });
  }

  /* ---------- 2. Price in ADD TO BAG + sticky ADD ---------- */
  function initAtcPrice(root, sid, cfg) {
    var btn = root.querySelector(
      'form[action^="/cart/add"] button[type="submit"], ' +
      'product-form button[type="submit"], ' +
      '[data-product-form] button[type="submit"]'
    );
    if (!btn) return;

    var qtyInput = root.querySelector('input[name="quantity"]');
    var stickyBtn = root.querySelector('.m-sticky-addtocart button[name="add"]');
    var stickyQtyInput = root.querySelector('.m-sticky-addtocart input[name="quantity"]');
    var domPrice = root.querySelector('.m-product__price, .price, [data-product-price]');

    function fmt(cents) {
      var formatted;
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && cfg.moneyFormat) {
        formatted = Shopify.formatMoney(cents, cfg.moneyFormat);
      } else {
        formatted = (cents / 100).toFixed(2);
      }
      var symbol = cfg.currencySymbol;
      if (symbol && formatted.indexOf(symbol) === -1) formatted = symbol + formatted;
      return formatted;
    }

    var data = readJSON(document.getElementById('nyx-variant-data-' + sid));
    if (!data) return;

    var opts = data.options || [];
    var variants = data.variants || [];
    var defaultVariantId = data.selected_variant_id;

    function currentOptions() {
      var out = {};
      // selects
      root.querySelectorAll('select[name^="options["]').forEach(function (el) {
        out[el.name] = el.value;
      });
      // radios
      root.querySelectorAll('input[type="radio"][name^="options["]:checked').forEach(function (el) {
        out[el.name] = el.value;
      });
      return out;
    }

    function defaultVariant() {
      return variants.find(function (v) { return v.id === defaultVariantId; }) || variants[0];
    }

    // the theme keeps the selected variant id in the form's [name="id"] field
    var idField = root.querySelector(
      'form[action^="/cart/add"] [name="id"], product-form [name="id"], [data-product-form] [name="id"]'
    );

    function byId(id) {
      id = Number(id);
      if (!id) return null;
      return variants.find(function (v) { return v.id === id; }) || null;
    }

    function findVariant() {
      if (!variants.length) return null;

      var selected = byId(idField && idField.value);
      if (selected) return selected;

      if (!opts.length) return defaultVariant();

      var wanted = currentOptions();
      var found = variants.find(function (v) {
        return opts.every(function (n, i) {
          return v.options && (v.options[i] == wanted['options[' + i + ']'] || v.options[i] == wanted[n]);
        });
      });

      return found || defaultVariant();
    }

    function getQty(input) {
      var q = parseInt(input && input.value, 10);
      return isNaN(q) || q < 1 ? 1 : q;
    }

    function priceEl(el) {
      var box = el.querySelector('.nyx-atc-price');
      if (!box) {
        box = document.createElement('span');
        box.className = 'nyx-atc-price';
        box.innerHTML =
          '<s class="nyx-atc-price__old"></s>' +
          '<span class="nyx-atc-price__now"></span>' +
          '<span class="nyx-atc-price__badge"></span>';
        el.appendChild(box);
      }
      return box;
    }

    function setPrice(el, now, old, badge) {
      if (!el || !now) return;
      var box = priceEl(el);
      box.querySelector('.nyx-atc-price__now').textContent = now;
      box.querySelector('.nyx-atc-price__old').textContent = old || '';
      box.querySelector('.nyx-atc-price__badge').textContent = badge || '';
    }

    function render(el, v, qty) {
      var onSale = v.compare_at_price > v.price;
      var pct = onSale ? Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) : 0;
      setPrice(
        el,
        fmt(v.price * qty),
        onSale ? fmt(v.compare_at_price * qty) : '',
        pct > 0 ? 'Save ' + pct + '%' : ''
      );
    }

    function update() {
      var v = findVariant();

      if (v && typeof v.price === 'number') {
        render(btn, v, getQty(qtyInput));
        if (stickyBtn) render(stickyBtn, v, getQty(stickyQtyInput));
        return;
      }
      if (domPrice) {
        var t = (domPrice.textContent || '').trim();
        setPrice(btn, t);
        setPrice(stickyBtn, t);
      }
    }

    update();

    root.addEventListener('change', function (e) {
      if (e.target.matches('select[name^="options["],input[type="radio"][name^="options["],input[name="quantity"]')) {
        update();
      }
    }, true);

    // swatches change the variant without a change event on options[...] inputs
    document.addEventListener('variant:changed', function () {
      requestAnimationFrame(update);
    });

    if (qtyInput) qtyInput.addEventListener('input', update);
    if (stickyQtyInput) stickyQtyInput.addEventListener('input', update);

    if (domPrice) {
      new MutationObserver(update).observe(domPrice, { childList: true, subtree: true, characterData: true });
    }
  }

  /* ---------- 3. Price + stock on one line ---------- */
  function initStockInline(root) {
    function priceWrap() {
      return root.querySelector('.m-product__price, .price');
    }

    function inventoryEl() {
      var list = root.querySelectorAll('.m-product__inventory, .inventory, [data-inventory], [data-inventory-count]');
      for (var i = 0; i < list.length; i++) {
        var el = list[i];
        if (el.closest('.nyx-stock-inline')) return el;
        if (el.offsetParent !== null && el.textContent.trim()) return el;
      }
      return null;
    }

    function placeInline() {
      var p = priceWrap(), inv = inventoryEl();
      if (!p || !inv) return;

      if (inv.closest('.nyx-stock-inline') && p.contains(inv.closest('.nyx-stock-inline'))) return;

      var inline = p.querySelector(':scope > .nyx-stock-inline');
      if (!inline) {
        inline = document.createElement('span');
        inline.className = 'nyx-stock-inline';
        p.appendChild(inline);
      }

      var oldParent = inv.parentElement;
      inline.appendChild(inv);

      setTimeout(function () {
        if (oldParent && !oldParent.closest('.nyx-stock-inline') && oldParent.textContent.trim() === '') {
          oldParent.classList.add('nyx-hide-original-inventory');
        }
      }, 0);
    }

    var placeInlineQueued = false;

    function schedulePlaceInline() {
      if (placeInlineQueued) return;
      placeInlineQueued = true;

      requestAnimationFrame(function () {
        placeInlineQueued = false;
        placeInline();
      });
    }

    placeInline();

    new MutationObserver(schedulePlaceInline).observe(root, {
      subtree: true,
      childList: true,
      characterData: true
    });

    root.addEventListener('change', schedulePlaceInline, true);
    document.addEventListener('variant:changed', schedulePlaceInline);
  }

  /* ---------- 4. Variant-aware gallery: swap slides on variant change ---------- */
  function initVariantGallery(root, sid) {
    var GALLERY_ID = 'MediaGallery-' + sid;
    var POOL_ID = 'variant-media-pool-' + sid;

    function getGallery() { return document.getElementById(GALLERY_ID); }
    function getPool()    { return document.getElementById(POOL_ID); }

    function waitForSlider(cb, tries) {
      tries = tries || 0;
      var g = getGallery();
      if (g && g.slider) { cb(g); return; }
      if (tries > 60) return;
      setTimeout(function () { waitForSlider(cb, tries + 1); }, 100);
    }

    // Override setActiveMediaForSlider so the theme doesn't navigate to the wrong position after a swap
    waitForSlider(function (gallery) {
      gallery.setActiveMediaForSlider = function () {
        if (!this.slider) return;
        var method = this.slider.slideToLoop ? 'slideToLoop' : 'slideTo';
        this.slider[method](0, 300, false);
      };
      requestAnimationFrame(function () {
        if (!gallery.slider) return;
        var method = gallery.slider.slideToLoop ? 'slideToLoop' : 'slideTo';
        gallery.slider[method](0, 0, false);
      });
    });

    // Swap slides on variant change (capture:true fires before the theme listener)
    document.addEventListener('variant:changed', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;

      var gallery = getGallery();
      var pool    = getPool();
      if (!gallery || !pool || !gallery.slider) return;

      var newVariantId = String(variant.id);
      var slider       = gallery.slider;

      var mainWrapper  = gallery.querySelector('.m-media-gallery__list');
      var thumbWrapper = gallery.querySelector('.nav-swiper-container .swiper-wrapper');
      var poolMain     = pool.querySelector('[data-pool-section="main"]');
      var poolThumb    = pool.querySelector('[data-pool-section="thumb"]');

      if (!mainWrapper || !poolMain) return;

      // --- Main slides ---
      // 1. Move current active slides to pool
      Array.from(mainWrapper.querySelectorAll(':scope > [data-variant-id]:not(.swiper-slide-duplicate)')).forEach(function (s) {
        poolMain.appendChild(s);
      });
      // 2. Remove Swiper loop duplicates
      Array.from(mainWrapper.querySelectorAll('.swiper-slide-duplicate')).forEach(function (d) { d.remove(); });
      // 3. Insert new variant slides from pool
      Array.from(poolMain.querySelectorAll('[data-variant-id="' + newVariantId + '"]')).forEach(function (s, i) {
        s.dataset.index = String(i);
        s.removeAttribute('data-swiper-slide-index');
        mainWrapper.appendChild(s);
      });

      // --- Thumbnails ---
      if (thumbWrapper && poolThumb) {
        Array.from(thumbWrapper.querySelectorAll(':scope > [data-variant-id]:not(.swiper-slide-duplicate)')).forEach(function (s) {
          poolThumb.appendChild(s);
        });
        Array.from(thumbWrapper.querySelectorAll('.swiper-slide-duplicate')).forEach(function (d) { d.remove(); });
        Array.from(poolThumb.querySelectorAll('[data-variant-id="' + newVariantId + '"]')).forEach(function (s, i) {
          s.dataset.index = String(i);
          s.removeAttribute('data-swiper-slide-index');
          thumbWrapper.appendChild(s);
        });
      }

      gallery.thumbItems = null;

      // --- Update Swiper ---
      try { if (typeof slider.loopDestroy === 'function') slider.loopDestroy(); } catch (err) {}
      try { if (typeof slider.loopCreate  === 'function') slider.loopCreate();  } catch (err) {}
      slider.update();

      setTimeout(function () {
        try {
          if (typeof slider.slideToLoop === 'function') {
            slider.slideToLoop(0, 0, false);
          } else {
            slider.slideTo(0, 0, false);
          }
          if (typeof gallery.updateThumbActiveState === 'function') gallery.updateThumbActiveState(0);
          if (typeof gallery.syncThumbPosition      === 'function') gallery.syncThumbPosition(0);
          if (gallery.navSlider && typeof gallery.navSlider.update === 'function') gallery.navSlider.update();
          if (typeof gallery.handleAutoplayVideo === 'function') gallery.handleAutoplayVideo();
        } catch (err) {}
      }, 0);
    }, true);
  }

  /* ---------- 5. maxmax product labels init ---------- */
  function initLabels(root, sid) {
    function run() {
      if (!window.MaxmaxProductSticker) return;
      window.MaxmaxProductSticker({
        rootId: root.id,
        galleryId: 'MediaGallery-' + sid,
        dataId: 'nyx-variant-data-' + sid
      });
    }
    // custom-product-labels.js is loaded (defer) before this file, so it is
    // normally ready here; DOMContentLoaded is the fallback.
    if (window.MaxmaxProductSticker) {
      run();
    } else {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    }
  }

  /* ---------- 6. Pay over time under shipping (mobile, page-level) ---------- */
  function movePayTerms() {
    if (window.innerWidth > 768) return;

    var payBlock = document.querySelector('shopify-payment-terms');
    var shippingBlock = document.querySelector('.main-product__block-shipping');

    if (payBlock && shippingBlock && shippingBlock.parentNode) {
      shippingBlock.style.marginBottom = '8px';
      shippingBlock.parentNode.insertBefore(payBlock, shippingBlock.nextSibling);
    }
  }

  /* ---------- boot ---------- */
  function init(root) {
    if (!root || root.__nyxPdp) return;
    root.__nyxPdp = true;

    var sid = root.getAttribute('data-section-id');
    if (!sid) return;
    var cfg = readJSON(root.querySelector('[data-nyx-pdp-config]')) || {};

    initMobileTop(root, sid);
    initAtcPrice(root, sid, cfg);
    initStockInline(root);
    initVariantGallery(root, sid);
    initLabels(root, sid);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.nyx-pdp').forEach(init);
  }

  initAll();
  onDomReady(movePayTerms);

  // Theme editor re-renders the section without re-running external scripts
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();